import { addSwipeSupport } from '../../../../core/utils/swipe-handlers.js';
import { interpolate } from '../../../translations.js';
import { resolveTextPlaceholders } from '../../../utils/placeholder-resolver.js';
import { createElement, getSettingsModalTranslationText } from './settings-dom-utils.js';
import { CREW_MAP, toIdString } from './settings-character-utils.js';

const slotClass = (index, extra = '') =>
    `settings-slot${extra ? ` ${extra}` : ''}${index === 0 ? '' : ' is-hidden'}`;

const resolveSettingsModalTemplate = (attrValue, fallback = '') => {
    const key = String(attrValue || '').replace(/^settings_modal\./, '');
    if (!key) return fallback;
    return getSettingsModalTranslationText(key, fallback);
};

const attachOutfitArrow = (button, direction, trigger) => {
    button.addEventListener('click', () => trigger(direction));
    button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            trigger(direction);
        }
    });
};

export const createCharacterSection = (allCharacters, activeCharacters) => {
    const crewSection = createElement('div', 'form-group');
    const crewTitle = createElement('h3', '', { textContent: 'Character Crew' });
    crewTitle.setAttribute('data-translate', 'settings_modal.character_crew');
    crewSection.appendChild(crewTitle);

    const numCharactersGroup = createElement('div', 'form-group');
    const numCharactersLabel = createElement('label', '', {
        for: 'num-allCharacters-settings',
        textContent: 'Number of Characters'
    });
    numCharactersLabel.setAttribute('data-translate', 'settings_modal.number_of_characters');

    const numCharactersSearchRoot = createElement('div', 'search-root', { 'data-search-root': 'num-allCharacters-settings' });
    const numCharactersSelect = createElement('select', 'settings-count-select', { id: 'num-allCharacters-settings' });

    [1, 2, 3].forEach(num => {
        const option = createElement('option', '', { value: num, textContent: num });
        if (num === Math.max(1, activeCharacters?.length || 1)) option.selected = true;
        numCharactersSelect.appendChild(option);
    });

    numCharactersGroup.appendChild(numCharactersLabel);
    numCharactersGroup.appendChild(numCharactersSearchRoot);
    numCharactersGroup.appendChild(numCharactersSelect);
    crewSection.appendChild(numCharactersGroup);

    const charSelectContainer = createElement('div', 'settings-block');
    const crewMap = CREW_MAP;

    const charRow = createElement('div', 'settings-crew-row');

    crewMap.forEach(({ charSelect }, index) => {
        const charDiv = createElement('div', slotClass(index), { id: `char-${index + 1}-section-settings` });

        const charLabel = createElement('label', '', { for: charSelect });
        charLabel.textContent = `Character ${index + 1}`;
        charLabel.setAttribute('data-translate', 'settings_modal.character_label');
        charLabel.setAttribute('data-settings-char-label', 'true');
        charLabel.dataset.number = String(index + 1);
        charLabel.dataset.slotIndex = String(index);

        const charSearchRoot = createElement('div', 'search-root', { 'data-search-root': charSelect });
        const charSelectEl = createElement('select', 'settings-full-select', { id: charSelect });

        charSelectEl.innerHTML = '<option value="">Select a character</option>';
        const firstCharacterOption = charSelectEl.querySelector('option');
        if (firstCharacterOption) {
            firstCharacterOption.setAttribute('data-translate', 'settings_modal.select_character');
        }
        allCharacters.forEach(char => {
            const option = createElement('option', '', { value: char.id, textContent: char.name });
            if (index < activeCharacters.length &&
                (activeCharacters[index]?.id === char.id.toString() || activeCharacters[index]?.id === char.id)) {
                option.selected = true;
            }
            charSelectEl.appendChild(option);
        });

        charDiv.appendChild(charLabel);
        charDiv.appendChild(charSearchRoot);
        charDiv.appendChild(charSelectEl);
        charRow.appendChild(charDiv);
    });

    charSelectContainer.appendChild(charRow);

    const roleRow = createElement('div', 'settings-crew-row settings-crew-row--roles');

    crewMap.forEach(({ roleSelect }, index) => {
        const roleDiv = createElement('div', slotClass(index), { id: `role-${index + 1}-section-settings` });

        const roleLabel = createElement('label', '', { for: roleSelect, textContent: 'Outfit' });
        roleLabel.setAttribute('data-translate', 'settings_modal.outfit');

        const roleSearchRoot = createElement('div', 'search-root', { 'data-search-root': roleSelect });
        const roleSelectEl = createElement('select', 'settings-full-select', { id: roleSelect });

        roleSelectEl.innerHTML = '<option value="">Select an outfit</option>';
        const firstOutfitOption = roleSelectEl.querySelector('option');
        if (firstOutfitOption) {
            firstOutfitOption.setAttribute('data-translate', 'settings_modal.select_outfit');
        }

        roleDiv.appendChild(roleLabel);
        roleDiv.appendChild(roleSearchRoot);
        roleDiv.appendChild(roleSelectEl);
        roleRow.appendChild(roleDiv);
    });

    charSelectContainer.appendChild(roleRow);

    const previewRow = createElement('div', 'settings-crew-row settings-crew-row--previews');

    crewMap.forEach(({ preview }, index) => {
        const previewDiv = createElement('div', slotClass(index), { id: `char-${index + 1}-preview-section-settings` });

        const previewWrapper = createElement('div', 'character-preview-wrapper');
        const previewCard = createElement('div', 'settings-preview-card', { id: preview });

        const lockBadge = createElement('div', 'settings-preview-lock-badge', {
            id: `${preview}-lock-badge`,
            'aria-label': 'Locked outfit',
            title: 'Locked outfit'
        });
        lockBadge.setAttribute('data-translate-attr', 'aria-label:settings_modal.locked_outfit');
        lockBadge.setAttribute('aria-hidden', 'true');
        lockBadge.innerHTML = '<i class="fas fa-lock"></i>';

        const previewIcon = createElement('i', 'fas fa-user settings-preview-icon');
        const previewPlaceholder = createElement('span', 'settings-preview-placeholder', {
            textContent: 'Character in role outfit will appear here'
        });
        previewPlaceholder.setAttribute('data-translate', 'settings_modal.character_preview_placeholder');
        previewCard.appendChild(previewIcon);
        previewCard.appendChild(previewPlaceholder);

        addSwipeSupport(previewCard, (direction) => {
            triggerArrowEvent(direction === 'left' ? 1 : -1);
        });

        const triggerArrowEvent = (direction) => {
            previewWrapper.dispatchEvent(new CustomEvent('settingsPreviewArrow', {
                bubbles: true,
                detail: { slotIndex: index, direction }
            }));
        };

        const leftArrow = createElement('button', 'preview-arrow preview-arrow--left is-hidden', {
            id: `preview-arrow-left-${index}`,
            type: 'button',
            'aria-label': 'Previous outfit',
            tabindex: '0'
        });
        leftArrow.setAttribute('data-translate-attr', 'aria-label:settings_modal.previous_outfit');
        leftArrow.innerHTML = '<i class="fas fa-chevron-left"></i>';
        attachOutfitArrow(leftArrow, -1, triggerArrowEvent);
        leftArrow.setAttribute('aria-hidden', 'true');

        const rightArrow = createElement('button', 'preview-arrow preview-arrow--right is-hidden', {
            id: `preview-arrow-right-${index}`,
            type: 'button',
            'aria-label': 'Next outfit',
            tabindex: '0'
        });
        rightArrow.setAttribute('data-translate-attr', 'aria-label:settings_modal.next_outfit');
        rightArrow.innerHTML = '<i class="fas fa-chevron-right"></i>';
        attachOutfitArrow(rightArrow, 1, triggerArrowEvent);
        rightArrow.setAttribute('aria-hidden', 'true');

        previewWrapper.appendChild(leftArrow);
        previewWrapper.appendChild(previewCard);
        previewWrapper.appendChild(lockBadge);
        previewWrapper.appendChild(rightArrow);

        previewDiv.appendChild(previewWrapper);
        previewRow.appendChild(previewDiv);
    });

    charSelectContainer.appendChild(previewRow);

    const characterGoalsRow = createElement('div', 'settings-goal-grid');

    crewMap.forEach((_, index) => {
        const goalDiv = createElement('div', slotClass(index, 'settings-goal-slot'), {
            id: `char-${index + 1}-goal-section-settings`
        });

        const goalGroup = createElement('div', 'form-group');

        const goalLabel = createElement('label', '', { for: `char-${index + 1}-goal-settings` });
        goalLabel.textContent = `Character ${index + 1} Goal`;
        goalLabel.setAttribute('data-translate', 'settings_modal.character_goal');
        goalLabel.setAttribute('data-settings-goal-label', 'true');
        goalLabel.dataset.number = String(index + 1);
        goalLabel.dataset.slotIndex = String(index);

        const goalTextarea = createElement('textarea', 'settings-goal-textarea', {
            id: `char-${index + 1}-goal-settings`,
            name: `char-${index + 1}-goal`
        });
        goalTextarea.placeholder = `What does Character ${index + 1} want to achieve? (e.g., 'Seduce {user}')`;
        goalTextarea.setAttribute('data-translate-placeholder', 'settings_modal.character_goal_placeholder');
        goalTextarea.setAttribute('data-settings-goal-placeholder', 'true');
        goalTextarea.dataset.number = String(index + 1);
        goalTextarea.dataset.slotIndex = String(index);

        goalGroup.appendChild(goalLabel);
        goalGroup.appendChild(goalTextarea);
        goalDiv.appendChild(goalGroup);
        characterGoalsRow.appendChild(goalDiv);
    });

    charSelectContainer.appendChild(characterGoalsRow);
    crewSection.appendChild(charSelectContainer);
    const getSlotName = (container, slotIndex, number) => {
        const charSelectId = crewMap[slotIndex]?.charSelect;
        const charSelect = charSelectId ? container.querySelector(`#${charSelectId}`) : null;
        const selectedValue = charSelect?.value ? String(charSelect.value).trim() : '';
        const selectedName = charSelect?.selectedOptions?.[0]?.textContent?.trim() || '';
        const fallbackName = `Character ${number}`;
        return selectedValue && selectedName ? selectedName : fallbackName;
    };

    const applyDynamicSectionTranslations = (container = crewSection) => {
        container.querySelectorAll('[data-settings-char-label], [data-settings-goal-label]').forEach((label) => {
            const number = Number(label.dataset.number);
            const slotIndex = Number(label.dataset.slotIndex);
            if (!Number.isFinite(number)) return;
            const name = getSlotName(container, slotIndex, number);
            const template = resolveSettingsModalTemplate(
                label.getAttribute('data-translate'),
                label.textContent || ''
            );
            label.textContent = interpolate(template, { number, name });
        });

        container.querySelectorAll('[data-settings-goal-placeholder]').forEach((textarea) => {
            const number = Number(textarea.dataset.number);
            const slotIndex = Number(textarea.dataset.slotIndex);
            if (!Number.isFinite(number)) return;
            const name = getSlotName(container, slotIndex, number);
            const template = resolveSettingsModalTemplate(
                textarea.getAttribute('data-translate-placeholder'),
                textarea.placeholder || ''
            );
            textarea.placeholder = resolveTextPlaceholders(
                interpolate(template, { number, name })
            );
        });

        container.querySelectorAll('.settings-preview-lock-badge').forEach((badge) => {
            const translatedLabel = badge.getAttribute('aria-label');
            if (translatedLabel) badge.setAttribute('title', translatedLabel);
        });
    };

    return { crewSection, numCharactersSelect, crewMap, applyDynamicSectionTranslations };
};

export class PreviewStateManager {
    constructor(crewMap) {
        this.crewMap = crewMap;
        this.previewState = crewMap.map(() => ({ outfits: [], currentIndex: 0 }));
    }

    updateArrowsVisibility(slotIndex) {
        const state = this.previewState[slotIndex];
        const leftArrow = document.getElementById(`preview-arrow-left-${slotIndex}`);
        const rightArrow = document.getElementById(`preview-arrow-right-${slotIndex}`);
        const shouldShow = !!(state && state.outfits && state.outfits.length > 1);

        [leftArrow, rightArrow].forEach((button) => {
            if (!button) return;
            button.classList.toggle('is-hidden', !shouldShow);
            button.disabled = !shouldShow;
            button.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
    }

    updateState(slotIndex, outfitsList = [], selectedOutfitId = '') {
        const normalized = (outfitsList || []).map(outfit => ({
            id: toIdString(outfit.id),
            name: outfit.name
        }));

        if (!normalized.length) {
            this.previewState[slotIndex] = { outfits: [], currentIndex: 0 };
            this.updateArrowsVisibility(slotIndex);
            return;
        }

        const targetId = toIdString(selectedOutfitId);
        let currentIndex = 0;
        if (targetId) {
            const foundIndex = normalized.findIndex(outfit => outfit.id === targetId);
            if (foundIndex >= 0) currentIndex = foundIndex;
        }

        this.previewState[slotIndex] = { outfits: normalized, currentIndex };
        this.updateArrowsVisibility(slotIndex);
    }

    handleArrowClick(slotIndex, direction) {
        if (typeof slotIndex !== 'number' || direction === 0) return;
        const state = this.previewState[slotIndex];
        if (!state || state.outfits.length <= 1) return;

        const total = state.outfits.length;
        state.currentIndex = (state.currentIndex + direction + total) % total;
        const nextOutfit = state.outfits[state.currentIndex];

        const roleSelectId = this.crewMap[slotIndex]?.roleSelect;
        const roleSelect = roleSelectId ? document.getElementById(roleSelectId) : null;

        if (roleSelect && nextOutfit) {
            roleSelect.value = nextOutfit.id;
            roleSelect.dispatchEvent(new Event('change'));
        }
    }

    getState(slotIndex) {
        return this.previewState[slotIndex];
    }
}

export function updateCharacterSectionsVisibility(crewMap, num, updatePreviewFn) {
    const isVisible2 = num >= 2;
    const isVisible3 = num === 3;

    crewMap.forEach(({ charSelect, roleSelect, preview }, index) => {
        if (index === 0) return;

        const sections = [
            `char-${index + 1}-section-settings`,
            `role-${index + 1}-section-settings`,
            `char-${index + 1}-preview-section-settings`,
            `char-${index + 1}-goal-section-settings`
        ];

        const isVisible = (index === 1 && isVisible2) || (index === 2 && isVisible3);

        sections.forEach(sectionId => {
            const section = document.getElementById(sectionId);
            if (section) section.classList.toggle('is-hidden', !isVisible);
        });

        if (!isVisible) {
            [charSelect, roleSelect, `char-${index + 1}-goal-settings`].forEach(id => {
                const el = document.getElementById(id);
                if (el) el.value = '';
            });
            if (preview && updatePreviewFn) updatePreviewFn(preview, null);
        }
    });
}
